import { useState, useRef, useEffect } from 'react';
import { chatService } from './services/api';
import './styles/App.css';
import { TrashIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import assistantAvatar from './assets/assistant-avatar.png';
import ameriaLogo from './assets/ameria-logo.png';
import LeadsView from './components/leads/LeadsView';
import ChatInput from './components/chat/ChatInput';
import ChatWindow from './components/chat/ChatWindow';
import ConversationList from './components/chat/ConversationList';
import AuthModal from './components/auth/AuthModal';
import Modal from './components/common/Modal';
import SettingsModals from './components/settings/SettingsModals';
import LanguageSwitcher from './components/common/LanguageSwitcher';
import useAuth from './hooks/useAuth';
import { useSettings } from './context/SettingsContext';
import { useLanguage } from './context/LanguageContext';
import config from './config';
import AdminDashboard from './components/admin/AdminDashboard';
import FileManager from './components/admin/FileManager';
import SessionExpiredModal from './components/auth/SessionExpiredModal';
import { useSessionExpired } from './hooks/useSessionExpired';

// Update the ASSISTANT_CONFIG to use the image from build directory
const ASSISTANT_CONFIG = {
  name: 'Maverick AI',
  avatar: assistantAvatar
};

const LOADING_MESSAGE = {
  role: 'assistant',
  content: '...',
  isLoading: true,
  timestamp: new Date().toISOString(),
};

function App() {
  const { t } = useLanguage();
  
  // Create dynamic welcome message based on current language
  const WELCOME_MESSAGE = {
    role: 'assistant',
    content: t('welcomeMessage'),
    timestamp: new Date().toISOString(),
  };

  // Add redirect check ok hello 
  useEffect(() => {
    let targetDomain;
    if (process.env.REACT_APP_ENV === 'local') {
      // No redirect needed for local development
      return;
    } else if (process.env.REACT_APP_ENV === 'staging') {
      targetDomain = 'ameria-chatbot-staging.reruption.com';
    } else if (process.env.REACT_APP_ENV === 'production') {
      targetDomain = 'ameria-chatbot.reruption.com';
    }

    // Only redirect if we have a target domain and we're not on the right domain/protocol
    if (targetDomain && (window.location.hostname !== targetDomain || window.location.protocol !== 'https:')) {
      window.location.href = `https://${targetDomain}${window.location.pathname}${window.location.search}${window.location.hash}`;
    }
  }, []);

  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [conversations, setConversations] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(() => {
    const savedId = localStorage.getItem('activeConversationId');
    return savedId || null;
  });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAdminDashboardModal, setShowAdminDashboardModal] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showFileManager, setShowFileManager] = useState(false);
  const auth = useAuth();
  const { session, signOut, userData } = auth;
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const adminMenuRef = useRef(null);
  const adminMenuTimeoutRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const {
    systemPrompt,
    isEditingPrompt,
    setIsEditingPrompt,
    showSettingsModal,
    setShowSettingsModal,
    isEditingVectorSettings,
    setIsEditingVectorSettings,
    isProductClassifierOpen,
    setIsProductClassifierOpen,
    isEditingVectorStorePrompt,
    setIsEditingVectorStorePrompt,
    vectorClassifierPrompt,
    setVectorClassifierPrompt,
    vectorClassifierThreshold,
    setVectorClassifierThreshold,
    settings,
    setSettings,
    isLoadingSettings,
    setIsLoadingSettings,
    settingsError,
    setSettingsError,
    vectorStorePrompt,
    setVectorStorePrompt,
    assistantId,
    setAssistantId,
    vectorstoreId,
    setVectorstoreId
  } = useSettings();

  const { isSessionExpiredModalOpen, handleCloseSessionExpiredModal } = useSessionExpired();

  useEffect(() => {
    const loadConversations = async () => {
      if (!session) {
        return;
      }

      try {
        const convs = await chatService.getConversations();
        setConversations(convs);
        
        if (activeConversationId) {
          const history = await chatService.getConversationHistory(activeConversationId);
          setMessages([WELCOME_MESSAGE, ...history.map(msg => ({
            role: msg.role,
            content: msg.content,
            sources: msg.sources,
            reasoning: msg.reasoning,
            isTyping: false,
            message_id: msg.message_id,
            debug: msg.debug
          }))]);
        } else {
          setMessages([WELCOME_MESSAGE]);
        }
      } catch (error) {
        if (activeConversationId) {
          setActiveConversationId(null);
          setMessages([WELCOME_MESSAGE]);
        }
      }
    };
    
    loadConversations();
  }, [session, activeConversationId]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (activeConversationId) {
      localStorage.setItem('activeConversationId', activeConversationId);
    } else {
      localStorage.removeItem('activeConversationId');
    }
  }, [activeConversationId]);

  const handleNewChat = async () => {
    // For logged-in users, generate title for current conversation
    if (activeConversationId && session) {
      chatService.generateTitle(activeConversationId)
        .then(titleResponse => {
          setConversations(prevConvs => {
            return prevConvs.map(conv => 
              conv.id === activeConversationId 
                ? { ...conv, title: titleResponse.title }
                : conv
            );
          });
        });
    }

    // Reset chat state for all users
    setMessages([WELCOME_MESSAGE]);
    setInputMessage('');
    setIsLoading(false);
    
    // Reset conversation state
    setActiveConversationId(null);
    setShowAdminDashboard(false);
    setShowFileManager(false);
  };

  // Admin menu hover handlers
  const handleAdminMenuEnter = () => {
    if (adminMenuTimeoutRef.current) {
      clearTimeout(adminMenuTimeoutRef.current);
    }
    setShowAdminMenu(true);
  };

  const handleAdminMenuLeave = () => {
    adminMenuTimeoutRef.current = setTimeout(() => {
      setShowAdminMenu(false);
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    try {
      setIsLoading(true);
      
      // Add user message
      const userMessage = {
        role: 'user',
        content: inputMessage.trim(),
        timestamp: new Date().toISOString()
      };
      
      setMessages(prevMessages => [...prevMessages, userMessage]);
      setInputMessage('');

      // Show loading message
      setMessages(prevMessages => [...prevMessages, LOADING_MESSAGE]);

      // Get response from API
      const response = await chatService.sendMessage(inputMessage.trim(), activeConversationId);
      
      // Remove loading message and add assistant response
      setMessages(prevMessages => {
        const messages = prevMessages.filter(msg => !msg.isLoading);
        // Update the last user message with its message_id
        const lastUserMessageIndex = messages.length - 1;
        if (messages[lastUserMessageIndex].role === 'user') {
          messages[lastUserMessageIndex] = {
            ...messages[lastUserMessageIndex],
            message_id: response.user_message_id // Add message_id from API response
          };
        }
        return [...messages, {
          role: 'assistant',
          content: response.response,
          sources: response.sources,
          reasoning: response.reasoning,
          timestamp: new Date().toISOString(),
          message_id: response.message_id,  // Preserve message_id from API response
          debug: response.debug  // Include debug information
        }];
      });

      // Update active conversation
      if (response.conversationId && !activeConversationId) {
        setActiveConversationId(response.conversationId);
        localStorage.setItem('activeConversationId', response.conversationId);
      }
    } catch (error) {
      setMessages(prevMessages => [...prevMessages, {
        role: 'assistant',
        content: t('errorMessage')
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConversationSelect = async (conversationId) => {
    try {
      setIsLoading(true);
      // Load conversation history
      const history = await chatService.getConversationHistory(conversationId);
      setMessages([WELCOME_MESSAGE, ...history.map(msg => ({
        role: msg.role,
        content: msg.content,
        sources: msg.sources,
        reasoning: msg.reasoning,
        isTyping: false,
        message_id: msg.message_id,
        debug: msg.debug
      }))]);
      
      // Update active conversation without changing cookie
      setActiveConversationId(conversationId);
      setShowAdminDashboard(false);  // Close admin dashboard
      setShowFileManager(false);     // Close file manager
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    try {
      await chatService.deleteConversation(conversationId);
      
      // If deleting active conversation, clear everything
      if (conversationId === activeConversationId) {
        document.cookie = 'conversation_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        localStorage.removeItem('selectedConversationId');
        setActiveConversationId(null);
        setMessages([WELCOME_MESSAGE]);
      }
      
      // Refresh conversations list without changing order
      const convs = await chatService.getConversations();
      setConversations(convs);
    } catch (error) {
    }
  };

  const handleUpdateSystemPrompt = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await chatService.updateSetting('system-prompt', systemPrompt);
      setIsEditingPrompt(false);
    } catch (error) {
      setSettingsError('Failed to update system prompt. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSaveVectorSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await Promise.all([
        chatService.updateSetting('vector-classifier-prompt', vectorClassifierPrompt),
        chatService.updateSetting('vector-classifier-threshold', vectorClassifierThreshold)
      ]);
      setIsEditingVectorSettings(false);
    } catch (error) {
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setIsLoadingSettings(true);
    setSettingsError(null);
    try {
      await Promise.all([
        chatService.updateSetting('product-classifier-prompt', settings['product-classifier-prompt']),
        chatService.updateSetting('product-classifier-threshold', settings['product-classifier-threshold'])
      ]);
      setIsProductClassifierOpen(false);
    } catch (error) {
      setSettingsError('Failed to save settings. Please try again.');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleUpdateVectorStorePrompt = async () => {
    try {
      setIsLoadingSettings(true);
      await chatService.updateVectorStoreSettings(
        vectorStorePrompt,
        assistantId,
        vectorstoreId
      );
      setIsEditingVectorStorePrompt(false);
    } catch (error) {
      setSettingsError('Failed to update vector store prompt');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleUpdateProductClassifierSettings = async () => {
    try {
      setIsLoadingSettings(true);
      await chatService.updateProductClassifierSettings(
        settings['product-classifier-prompt'],
        settings['product-classifier-threshold']
      );
      setIsProductClassifierOpen(false);
    } catch (error) {
      setSettingsError('Failed to update product classifier settings');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  useEffect(() => {
    document.title = 'AMERIA Investment Consultant';
  }, []);

  // Update the auth listener
  useEffect(() => {
    const { data: { subscription } } = auth;
    return () => {
      subscription?.unsubscribe();
    };
  }, [auth]);

  // Save active conversation to localStorage whenever it changes
  useEffect(() => {
    if (activeConversationId) {
      localStorage.setItem('selectedConversationId', activeConversationId);
    } else {
      localStorage.removeItem('selectedConversationId');
    }
  }, [activeConversationId]);

  // Close admin menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
        setShowAdminMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex h-screen">
      {/* Mobile menu button */}
      <button 
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-2 right-2 z-50 p-2 rounded-md bg-gray-800 text-white"
      >
        {isMobileMenuOpen ? (
          <XMarkIcon className="h-6 w-6" />
        ) : (
          <Bars3Icon className="h-6 w-6" />
        )}
      </button>

      {/* Sidebar */}
      <div className={`${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:relative w-64 bg-gray-800 text-white flex flex-col h-full transition-transform duration-300 ease-in-out z-40`}>
        <div className="flex-none p-4">
          <div className="h-12">
            <img src={ameriaLogo} alt="AMERIA Logo" className="h-full object-contain object-left" />
          </div>
        </div>
        
        {/* New request button */}
        <div className="flex-none p-4">
          <button 
            onClick={handleNewChat}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600"
          >
            {t('newChat')}
          </button>
        </div>

        {/* Conversations section */}
        <div className="flex-1 overflow-y-auto p-4">
          {session && <h2 className="text-sm font-semibold mb-4">{t('conversations')}</h2>}
          <div className="space-y-2">
            {session && activeConversationId && !conversations.find(c => c.id === activeConversationId) ? (
              // Show current chat while it's being created
              <div className={`p-2 bg-gray-700 rounded cursor-pointer flex justify-between items-center border-l-4 border-blue-500`}>
                <div className="truncate flex-1">
                  {t('currentChat')}
                </div>
              </div>
            ) : null}
            {session && conversations.map((conv) => (
              <div 
                key={conv.id}
                className={`p-2 hover:bg-gray-700 rounded cursor-pointer flex justify-between items-center
                  ${activeConversationId === conv.id ? 'bg-gray-700 border-l-4 border-blue-500' : ''}`}
                onClick={() => handleConversationSelect(conv.id)}
              >
                <div className="truncate flex-1">
                  {conv.title || t('newChatTitle')}
                </div>
                <button
                  onClick={(e) => handleDeleteConversation(e, conv.id)}
                  className="p-1 hover:bg-gray-600 rounded"
                >
                  <TrashIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Settings section */}
        <div className="flex-none p-4 border-t border-gray-700">
          {session ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-sm font-semibold text-white">
                  {t('settings')}
                </h2>
                <button
                  onClick={signOut}
                  className="p-1 hover:bg-gray-600 rounded"
                  title={t('signOut')}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 text-gray-400 hover:text-white" />
                </button>
              </div>
              {userData && (
                <div className="space-y-4">
                  {/* User Info Section */}
                  <div className="p-2 rounded bg-gray-700/50">
                    <div className="text-sm text-gray-300 truncate">
                      {session.user.email}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {t('status')}: {userData.is_admin ? t('administrator') : t('user')}
                    </div>
                  </div>

                  {/* Admin Dashboard button for all admins */}
                  {userData.is_admin && (
                    <button
                      onClick={() => {
                        setShowAdminDashboard(true);
                        setShowFileManager(false);
                      }}
                      className="w-full text-left p-2 rounded text-sm hover:bg-gray-700 flex items-center justify-between"
                    >
                      <span>Admin Dashboard</span>
                    </button>
                  )}

                  {/* File Manager button for admins and specific users */}
                  {(userData.is_admin || userData.email === 'n@pmwh.eu' || userData.email === 'christian.ensslen@ameria.com') && (
                    <button
                      onClick={() => {
                        setShowFileManager(true);
                        setShowAdminDashboard(false);
                      }}
                      className="w-full text-left p-2 rounded text-sm hover:bg-gray-700 flex items-center justify-between"
                    >
                      <span>File Manager</span>
                    </button>
                  )}

                  {/* Super-Admin Settings with hover menu */}
                  {userData.is_admin && (userData.email === 'n@pmwh.eu' || userData.email === 'christian.ensslen@ameria.com') && (
                    <>
                      <div className="space-y-2" ref={adminMenuRef}>
                        <div 
                          onMouseEnter={handleAdminMenuEnter}
                          onMouseLeave={handleAdminMenuLeave}
                          className="relative"
                        >
                          <button className="w-full text-left p-2 rounded text-sm hover:bg-gray-700 flex items-center justify-between">
                            <span>Super-Admin Settings</span>
                            <span className="text-gray-400">▸</span>
                          </button>
                          
                          {showAdminMenu && (
                            <div 
                              onMouseEnter={handleAdminMenuEnter}
                              onMouseLeave={handleAdminMenuLeave}
                              className="absolute left-full bottom-0 ml-1 bg-gray-800 rounded-lg shadow-lg min-w-[220px]"
                              style={{ 
                                padding: '8px',
                                transform: 'translateY(0%)',
                                marginBottom: '-8px' 
                              }}
                            >
                              {/* Bridge gap between main button and submenu */}
                              <div className="absolute left-[-10px] top-0 bottom-0 w-[10px]" />
                              
                              <button
                                onClick={() => {
                                  setIsEditingPrompt(true);
                                  setShowAdminMenu(false);
                                }}
                                className="w-full text-left mb-1 px-4 py-3 rounded hover:bg-gray-700 text-sm transition-colors duration-150"
                              >
                                Generelle Anweisungen
                              </button>

                              <button
                                onClick={() => {
                                  setIsEditingVectorSettings(true);
                                  setShowAdminMenu(false);
                                }}
                                className="w-full text-left px-4 py-3 rounded hover:bg-gray-700 text-sm transition-colors duration-150"
                              >
                                Vektor-Klassifikator Anweisung
                              </button>
                              
                              <button
                                onClick={() => {
                                  setIsProductClassifierOpen(true);
                                  setShowAdminMenu(false);
                                }}
                                className="w-full text-left px-4 py-3 rounded hover:bg-gray-700 text-sm transition-colors duration-150"
                              >
                                Produkt-Klassifikator Einstellungen
                              </button>
                              
                              <button
                                onClick={() => {
                                  setIsEditingVectorStorePrompt(true);
                                  setShowAdminMenu(false);
                                }}
                                className="w-full text-left px-4 py-3 rounded hover:bg-gray-700 text-sm transition-colors duration-150"
                              >
                                Vektor-Speicher Anweisung
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 hover:text-white"
            >
              {t('signIn')}
            </button>
          )}
        </div>
      </div>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Add AuthModal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        setMessages={setMessages}
        setConversations={setConversations}
      />

      {/* Add Settings Modals */}
      <SettingsModals />

      {/* Add SessionExpiredModal */}
      <SessionExpiredModal 
        open={isSessionExpiredModalOpen}
        onClose={handleCloseSessionExpiredModal}
      />

      {/* Main content area */}
      {showAdminDashboard ? (
        <AdminDashboard />
      ) : showFileManager ? (
        <FileManager />
      ) : (
        /* Main chat area */
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="bg-white shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {/* Pulsing status indicator */}
                <div className="relative">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                </div>
                <h1 className="text-xl font-bold">{t('connectedTo')}</h1>
              </div>
              <LanguageSwitcher />
            </div>
          </header>

          {/* Chat messages */}
          <div className="flex-1 overflow-auto p-4 space-y-4">
            <ChatWindow 
              messages={messages} 
              conversationId={activeConversationId}
              isAuthenticated={!!session}
            />
          </div>

          {/* Input form */}
          <ChatInput 
            handleSubmit={handleSubmit} 
            inputMessage={inputMessage} 
            setInputMessage={setInputMessage} 
            isLoading={isLoading} 
          />
        </div>
      )}
    </div>
  );
}

export default App;
